import { teamsClient } from '@atlaskit/teams-client';
import { type TeamWithImageUrls } from '@atlaskit/teams-client/types';

import { type TeamsPermissions } from '../../hooks/use-team-visibility';
import type { StoreApi, TeamAppState as TeamState } from '../../types';

export const removeMemberState = (
	teamState: TeamState,
	memberId: string,
	permissions?: Partial<TeamsPermissions>,
): TeamState => {
	const { exitedTeams, myTeamMembership, teamMembers, teamProfile } = teamState;
	const teamId = myTeamMembership.teamId;
	const myMembership = myTeamMembership.membership;
	const isCurrentUser = myMembership && myMembership.membershipId.memberId === memberId;

	const finalMembers = {
		...teamMembers,
		teamId,
		hasLoaded: true,
		hasError: false,
		isLoading: false,
		errorType: null,
		members: teamMembers.members.filter((member) => !!member.user && member.user.id !== memberId),
	};

	if (!isCurrentUser) {
		return {
			...teamState,
			teamMembers: finalMembers,
		};
	}

	// No longer a part of this team, hide it from search results
	exitedTeams.add(teamId);

	const currentTeam = teamProfile.data;

	/**
	 * Allow admins to manage teams after they are removed from the team
	 * If currentTeam.permission is not undefined and user is not admin, they can only view the team after removal, set permission to FULL_READ
	 * If currentTeam.permission is not undefined and user is admin, they can still manage the team after removal, set permission to FULL_WRITE
	 * If currentTeam.permission is undefined, don't override the permission
	 */
	const permissionAfterRemoval = currentTeam.permission
		? permissions?.canAdminTeams
			? 'FULL_WRITE'
			: 'FULL_READ'
		: currentTeam.permission;

	// Nullify myMembership and change permission
	return {
		...teamState,
		teamMembers: finalMembers,
		myTeamMembership: {
			teamId,
			hasLoaded: true,
			membership: undefined,
		},
		teamProfile: {
			teamId,
			isLoading: false,
			hasLoaded: true,
			errorType: null,
			data: {
				...(currentTeam as TeamWithImageUrls),
				permission: permissionAfterRemoval,
			},
		},
	};
};

export const removeTeamMember =
	(teamId: string, memberId: string, permissions?: Partial<TeamsPermissions>) =>
	async ({ getState, setState }: StoreApi): Promise<void> => {
		const state = getState();
		const removeMembershipResponse = await teamsClient.removeTeamMemberships(teamId, [
			{
				accountId: memberId,
			},
		]);

		// as we only removing 1 member at a time, it is safe to say task failed with even 1 error
		if (removeMembershipResponse.errors && removeMembershipResponse.errors.length) {
			throw Error('Membership claims to be removed but was not');
		}

		if (state.teamMembers.teamId !== teamId) {
			return;
		}

		const nextState = removeMemberState(state, memberId, permissions);
		setState(nextState);
	};
